import React, { useState, useEffect, useRef } from 'react';
import { InputAdornment, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { TextField } from '../TextField';

const ColourPreview = styled(Box)(({ theme }) => ({
  width: 24,
  height: 24,
  borderRadius: '8px',
  border: `1px solid ${theme.palette.grey[300]}`,
  backgroundColor: '#ffffff',
  cursor: 'pointer',
  position: 'relative',
}));

const HiddenColourInput = styled('input')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  opacity: 0,
  cursor: 'pointer',
});

interface ColourInputProps {
  value?: string;
  onChange: (color: string) => void;
  error?: boolean;
  id?: string;
  helperText?: string;
  disabled?: boolean;
  placeholder?: string;
  size?: 'small' | 'medium';
  label?: string;
  required?: boolean;
  onBlur?: () => void;
  t: (key: string) => string;
  ariaLabel?: string;
  fullWidth?: boolean;
  inputRef?: React.Ref<any>;
}

const isValidHex = (color: string): boolean => {
  return /^#([A-Fa-f0-9]{6})$/.test(color);
};

export const ColourInput: React.FC<ColourInputProps> = ({
  value = '',
  onChange,
  error = false,
  helperText,
  id,
  disabled = false,
  placeholder = '#000000',
  label = 'Color',
  required = false,
  size = 'medium',
  onBlur = () => {},
  t,
  ariaLabel,
  fullWidth = false,
  inputRef,
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [isValid, setIsValid] = useState(true);
  const colorInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = event.target.value;
    if (newValue && newValue[0] !== '#') {
      newValue = '#' + newValue;
    }
    setInputValue(newValue);

    if (isValidHex(newValue)) {
      setIsValid(true);
    } else {
      setIsValid(false);
    }
    onChange(newValue);
  };

  const handleBlur = () => {
    if (!isValidHex(inputValue)) {
      setInputValue(value);
      setIsValid(true);
    }
    onBlur?.();
  };

  const handleColorPickerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setInputValue(newValue);
    setIsValid(true);
    onChange(newValue);
  };

  return (
    <TextField
      value={inputValue}
      id={id}
      onChange={handleChange}
      onBlur={handleBlur}
      sx={{
        '& .MuiInputBase-root': {
          '&.Mui-disabled': {
            backgroundColor: '#F2F4F7',
          },
        },
      }}
      size={size}
      error={error || (!isValid && inputValue !== '')}
      helperText={
        helperText || (!isValid && inputValue !== '' ? t('common:colour_input:invalid_hex_error') : undefined)
      }
      disabled={disabled}
      placeholder={placeholder}
      label={label}
      required={required}
      inputProps={{
        'aria-label': ariaLabel,
      }}
      fullWidth={fullWidth}
      inputRef={inputRef}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <ColourPreview sx={{ backgroundColor: isValidHex(inputValue) ? inputValue : '#ffffff' }}>
                <HiddenColourInput
                  type="color"
                  value={isValidHex(inputValue) ? inputValue : '#ffffff'}
                  onChange={handleColorPickerChange}
                  disabled={disabled}
                  ref={colorInputRef}
                />
              </ColourPreview>
            </InputAdornment>
          ),
        },
      }}
    />
  );
};
